import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Bell,
  Briefcase,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  DollarSign,
  Mail,
  RefreshCw,
  Upload,
  X,
  XCircle,
  Activity,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../lib/formatters';
import { mergeFeed, feedCounts } from './notificationsFeedModel';
import { planForFeedItem, planForAttention, EMAIL_LABEL } from './notificationActionsModel';
import SenderSelect from '../email/SenderSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DISMISS_KEY = 'pfs.dashboard.dismissals.v1';

const todayISO = () => new Date().toISOString().slice(0, 10);

const loadDismissals = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Event-marker dismissals (e.g. "2026-03-16|29", "Funded|1234") persist
    // forever — they only "expire" when the alert's marker changes (new event).
    // Only day-scoped entries (plain YYYY-MM-DD values, e.g. the upload
    // banner) are pruned when the day has passed.
    const today = todayISO();
    const fresh = {};
    Object.entries(parsed).forEach(([k, v]) => {
      const isDayScoped = /^\d{4}-\d{2}-\d{2}$/.test(String(v));
      if (!isDayScoped || v === today) fresh[k] = v;
    });
    return fresh;
  } catch {
    return {};
  }
};

const saveDismissals = (d) => {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(d));
  } catch {
    /* noop */
  }
};

const StatTile = ({ icon: Icon, label, value, sub, accent = 'blue', testid, onClick }) => {
  const accents = {
    blue: 'text-blue-300 bg-blue-500/10 border-blue-500/20',
    green: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20',
    red: 'text-red-300 bg-red-500/10 border-red-500/20',
    amber: 'text-amber-300 bg-amber-500/10 border-amber-500/20',
    slate: 'text-slate-300 bg-slate-500/10 border-slate-500/20',
  };
  return (
    <Card
      className={`bg-slate-900/60 border ${accents[accent]} backdrop-blur ${
        onClick ? 'cursor-pointer hover:ring-2 hover:ring-slate-400/40 transition' : ''
      }`}
      data-testid={testid}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
            {label}
          </span>
          {Icon && <Icon className="h-4 w-4 text-slate-400" />}
        </div>
        <div className="text-2xl font-bold text-white tabular-nums">{value}</div>
        {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
};

// 10/08 (Josh): every card can be minimised with the chevron; always EXPANDED on page load (not remembered).
const SectionCard = ({ title, icon: Icon, children, action, testid, className = '' }) => {
  const [open, setOpen] = useState(true);
  return (
    <Card
      className={`bg-slate-900/60 border border-slate-700/60 backdrop-blur ${className}`}
      data-testid={testid}
    >
      <CardContent className="p-0">
        <div className={`flex items-center justify-between px-4 py-3 ${open ? 'border-b border-slate-700/60' : ''}`}>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-2 text-left hover:text-white"
            aria-expanded={open}
            data-testid={testid ? `${testid}-toggle` : undefined}
            title={open ? 'Minimize' : 'Expand'}
          >
            {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
            {Icon && <Icon className="h-4 w-4 text-slate-300" />}
            <h3 className="text-sm font-semibold text-slate-100">{title}</h3>
          </button>
          {open && action}
        </div>
        {open && <div className="divide-y divide-slate-800">{children}</div>}
      </CardContent>
    </Card>
  );
};

const EmptyRow = ({ text }) => (
  <div className="px-4 py-6 text-center text-xs text-slate-500 flex items-center justify-center gap-2">
    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
    {text}
  </div>
);

const formatRelative = (iso) => {
  if (!iso) return 'never';
  try {
    const d = new Date(iso);
    const diffH = (Date.now() - d.getTime()) / 3600000;
    if (diffH < 1) return `${Math.round(diffH * 60)}m ago`;
    if (diffH < 24) return `${Math.round(diffH)}h ago`;
    return `${Math.round(diffH / 24)}d ago`;
  } catch {
    return iso;
  }
};

const NotificationDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dismissals, setDismissals] = useState(loadDismissals());
  // Bounce Notice email dialog (Josh 08/10: click -> email the broker)
  const [emailDlg, setEmailDlg] = useState(null); // {dealUuid, dealNum, to, subject, body, warnings, transactionId, sending}
  // 08/10 (Josh): tiles must be clickable to see the alerts behind them —
  // clicking reveals the section INCLUDING dismissed rows so counts match.
  const [showDismissed, setShowDismissed] = useState({ bounce: false, attn: false });
  const navigate = useNavigate();

  const jumpToSection = (testid, section) => {
    if (section) setShowDismissed((s) => ({ ...s, [section]: true }));
    setTimeout(() => {
      document
        .querySelector(`[data-testid="${testid}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/dashboard/notifications`, {
        headers: getAuthHeaders(),
      });
      setData(res.data);
      setError(null);
    } catch (e) {
      console.error('Dashboard fetch failed', e);
      const detail =
        e?.response?.data?.detail ||
        e?.response?.status
          ? `HTTP ${e.response.status}${e.response.data?.detail ? ' — ' + e.response.data.detail : ''}`
          : e?.message || 'Network error';
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 10/07 (Josh): "the recent activity tab should be combined with the notifications box" —
  // the home-screen card merges MY notifications (same source as the bell) with upload activity.
  const [notifs, setNotifs] = useState([]);
  const fetchNotifs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { headers: getAuthHeaders(), params: { limit: 15 } });
      setNotifs(res.data?.notifications || []);
    } catch {
      /* the card still shows upload activity */
    }
  }, [getAuthHeaders]);
  useEffect(() => {
    fetchNotifs();
  }, [fetchNotifs]);
  const markRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, null, { headers: getAuthHeaders() });
      setNotifs((list) => list.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch {
      /* noop */
    }
  };
  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`, null, { headers: getAuthHeaders() });
      setNotifs((list) => list.map((n) => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Failed to mark all as read');
    }
  };
  // 10/08 (Josh): a click EXPANDS the row with the actions that fit it (email the merchant / broker, open the
  // deal, open the day's import tally, list the candidate deals behind an aggregate alert) instead of navigating.
  const [expandedFeed, setExpandedFeed] = useState(null);
  const [expandedAttn, setExpandedAttn] = useState(null);
  const [cands, setCands] = useState({}); // job → {loading, rows, date}
  const openFeedItem = (it) => {
    if (it.kind === 'notification' && it.unread) markRead(it.id);
    setExpandedFeed((k) => (k === it.key ? null : it.key));
    const plan = planForFeedItem(it);
    if (plan?.candidatesJob && !cands[plan.candidatesJob]) loadCandidates(plan.candidatesJob);
  };
  const loadCandidates = async (job) => {
    setCands((c) => ({ ...c, [job]: { loading: true, rows: [] } }));
    try {
      const res = await axios.get(`${API}/outreach/candidates`, { headers: getAuthHeaders(), params: { job } });
      setCands((c) => ({ ...c, [job]: { loading: false, rows: res.data?.rows || [], date: res.data?.date, kind: res.data?.kind } }));
    } catch (e) {
      setCands((c) => ({ ...c, [job]: { loading: false, rows: [], error: e.response?.data?.detail || e.message } }));
    }
  };
  const openOutreachEmail = async (dealId, kind, role = 'merchant') => {
    if (kind === 'bounce') return openBounceEmail({ deal_id: dealId });
    setEmailDlg({ mode: 'outreach', dealUuid: dealId, kind, toRole: role, loading: true });
    try {
      const res = await axios.get(`${API}/outreach/${dealId}/preview`, { headers: getAuthHeaders(), params: { kind, to: role } });
      const p = res.data;
      setEmailDlg({ mode: 'outreach', dealUuid: dealId, dealNum: p.deal_number, kind, toRole: role, to: p.to || '', cc: p.cc || '',
        recipients: p.recipients || [], subject: p.subject || '', body: p.body || '', warnings: p.warnings || [], loading: false, sending: false,
        sender: p.sender || null, senderId: p.sender?.default_id || '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to build email');
      setEmailDlg(null);
    }
  };
  const sendOutreachEmail = async () => {
    if (!emailDlg) return;
    setEmailDlg((d) => ({ ...d, sending: true }));
    try {
      await axios.post(`${API}/outreach/${emailDlg.dealUuid}/send`,
        { to: emailDlg.to, cc: emailDlg.cc || '', subject: emailDlg.subject, body: emailDlg.body, kind: emailDlg.kind,
          to_role: emailDlg.toRole, sender_id: emailDlg.senderId || null },
        { headers: getAuthHeaders() });
      toast.success(`Email sent to ${emailDlg.to}`);
      setEmailDlg(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
      setEmailDlg((d) => (d ? { ...d, sending: false } : d));
    }
  };
  const sendEmail = () => (emailDlg?.mode === 'outreach' ? sendOutreachEmail() : sendBounceEmail());
  const EmailBtn = ({ dealId, kind, role, label }) => (
    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openOutreachEmail(dealId, kind, role); }}
      className="h-7 text-xs border-amber-500/40 text-amber-200 hover:bg-amber-500/10" data-testid={`email-${kind}-${role}-${dealId}`}>
      <Mail className="h-3.5 w-3.5 mr-1" />{label || `Email ${role}`}
    </Button>
  );
  const ActionPanel = ({ plan, message }) => {
    if (!plan) return null;
    const job = plan.candidatesJob ? cands[plan.candidatesJob] : null;
    return (
      <div className="mt-2 ml-8 rounded border border-slate-600/60 bg-slate-800/60 p-3 text-xs text-slate-200 space-y-2" onClick={(e) => e.stopPropagation()}>
        {message && <div className="whitespace-pre-wrap text-slate-200">{message}</div>}
        <div className="flex flex-wrap items-center gap-2">
          {plan.email && plan.email.roles.map((r) => (
            <EmailBtn key={r} dealId={plan.dealId} kind={plan.email.kind} role={r} label={`Email ${r}: ${EMAIL_LABEL[plan.email.kind] || plan.email.kind}`} />
          ))}
          {plan.dealId && (
            <Link to={`/deals/${plan.dealId}`}><Button size="sm" variant="ghost" className="h-7 text-xs text-slate-200 hover:text-white hover:bg-slate-700">Open deal <ChevronRight className="h-3.5 w-3.5 ml-1" /></Button></Link>
          )}
          {plan.href && !plan.dealId && (
            <Link to={plan.href}><Button size="sm" variant="ghost" className="h-7 text-xs text-slate-200 hover:text-white hover:bg-slate-700">
              {plan.kind === 'upload' || plan.kind === 'reconciliation' ? `Open the day's tally${plan.importDate ? ` (${plan.importDate})` : ''}` : 'Open'} <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button></Link>
          )}
          {plan.kind === 'reminder' && <Link to="/notifications"><Button size="sm" variant="ghost" className="h-7 text-xs text-slate-200 hover:text-white hover:bg-slate-700">All reminders</Button></Link>}
        </div>
        {plan.candidatesJob && (
          <div className="pt-1 border-t border-slate-700/60">
            {job?.loading && <div className="text-slate-400">Loading deals…</div>}
            {job?.error && <div className="text-red-300">{job.error}</div>}
            {job && !job.loading && !job.error && job.rows.length === 0 && <div className="text-slate-400">No report rows found for this alert.</div>}
            {job && !job.loading && job.rows.length > 0 && (
              <div className="space-y-1">
                <div className="text-[11px] uppercase tracking-wide text-slate-400">Deals in this alert{job.date ? ` · report ${job.date}` : ''}</div>
                {job.rows.map((r) => (
                  <div key={r.deal_uuid || r.label} className="flex flex-wrap items-center justify-between gap-2 py-1 border-b border-slate-700/40 last:border-0">
                    <div className="text-slate-100">
                      {r.label || r.deal_number}
                      {r.pct_repaid != null && <span className="text-slate-400 ml-2">{r.pct_repaid}% repaid</span>}
                      {r.pct_remaining != null && <span className="text-slate-400 ml-2">{r.pct_remaining}% left</span>}
                      {r.balance != null && <span className="text-slate-400 ml-2">· {formatCurrency(r.balance)}</span>}
                      {r.dates && <span className="text-slate-400 ml-2">· missed {r.dates.join(', ')}</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      {r.deal_uuid && <EmailBtn dealId={r.deal_uuid} kind={job.kind} role="merchant" label="Email merchant" />}
                      {r.deal_uuid && <EmailBtn dealId={r.deal_uuid} kind={job.kind} role="broker" label="Email broker" />}
                      {r.deal_uuid && <Link to={`/deals/${r.deal_uuid}`}><Button size="sm" variant="ghost" className="h-7 text-xs text-slate-200 hover:text-white hover:bg-slate-700">View</Button></Link>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // 08/06 (Josh): no background polling — data refreshes when the page is
  // accessed (mount fetch above + tab-focus refetch here) and via the manual
  // Refresh button. Silent: no spinner flash, keeps last good data on error.
  const [refreshing, setRefreshing] = useState(false);
  const silentRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/dashboard/notifications`, {
        headers: getAuthHeaders(),
      });
      setData(res.data);
      setError(null);
      fetchNotifs();
    } catch {
      /* keep showing last good data */
    } finally {
      setRefreshing(false);
    }
  }, [getAuthHeaders, fetchNotifs]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') silentRefresh();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [silentRefresh]);

  // 08/06 (Josh): dismissals are EVENT-AWARE and persistent — a dismissed
  // alert stays hidden until something NEW happens on it (its marker changes),
  // instead of resurrecting every morning. The upload banner stays day-scoped.
  const openBounceEmail = async (b) => {
    setEmailDlg({ mode: 'bounce', dealUuid: b.deal_id, dealNum: b.deal_id_number, loading: true });
    try {
      const res = await axios.get(`${API}/bounce-notice/${b.deal_id}/preview`, {
        headers: getAuthHeaders(),
      });
      const p = res.data;
      setEmailDlg({
        mode: 'bounce',
        dealUuid: b.deal_id,
        dealNum: p.deal_number,
        to: p.to || '',
        subject: p.subject || '',
        body: p.body || '',
        warnings: p.warnings || [],
        transactionId: p.transaction_id,
        sender: p.sender || null,
        senderId: p.sender?.default_id || '',
        loading: false,
        sending: false,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to build bounce notice');
      setEmailDlg(null);
    }
  };

  const sendBounceEmail = async () => {
    if (!emailDlg) return;
    setEmailDlg((d) => ({ ...d, sending: true }));
    try {
      await axios.post(
        `${API}/bounce-notice/${emailDlg.dealUuid}/send`,
        {
          to: emailDlg.to,
          subject: emailDlg.subject,
          body: emailDlg.body,
          transaction_id: emailDlg.transactionId,
          sender_id: emailDlg.senderId || null,
        },
        { headers: getAuthHeaders() }
      );
      toast.success(`Bounce notice sent to ${emailDlg.to}`);
      setEmailDlg(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send bounce notice');
      setEmailDlg((d) => (d ? { ...d, sending: false } : d));
    }
  };

  const bounceMarker = (b) => `${b.last_bounce_date || ''}|${b.consecutive_count || 0}`;
  const attnMarker = (n) => `${n.status || ''}|${n.balance ?? ''}`;
  const actionMarker = (p) => `${p.count ?? ''}`;

  // Bulk dismiss (Josh 08/10: bulletin "clear all or one by one")
  const dismissMany = (pairs) => {
    const next = { ...dismissals };
    pairs.forEach(([id, marker]) => { next[id] = String(marker); });
    setDismissals(next);
    saveDismissals(next);
  };
  // not_paying lines are event-scoped: they stay cleared until the deal's
  // status or balance changes (a payment resuming removes them naturally).
  const npMarker = (d) => `${d.status || ''}|${d.balance ?? ''}`;

  const dismiss = (id, marker = todayISO()) => {
    const next = { ...dismissals, [id]: String(marker) };
    setDismissals(next);
    saveDismissals(next);
  };

  const isDismissed = (id, marker = todayISO()) => dismissals[id] === String(marker);

  const visibleBounceAlerts = useMemo(
    () =>
      showDismissed.bounce
        ? data?.bounce_alerts || []
        : (data?.bounce_alerts || []).filter((b) => !isDismissed(`bounce:${b.deal_id}`, bounceMarker(b))),
    [data, dismissals, showDismissed.bounce], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const visibleNeedsAttention = useMemo(
    () =>
      showDismissed.attn
        ? data?.needs_attention || []
        : (data?.needs_attention || []).filter((n) => !isDismissed(`attn:${n.deal_id}`, attnMarker(n))),
    [data, dismissals, showDismissed.attn], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const dismissedBounceCount =
    (data?.bounce_alerts?.length ?? 0) -
    (data?.bounce_alerts || []).filter((b) => !isDismissed(`bounce:${b.deal_id}`, bounceMarker(b))).length;
  const dismissedAttnCount =
    (data?.needs_attention?.length ?? 0) -
    (data?.needs_attention || []).filter((n) => !isDismissed(`attn:${n.deal_id}`, attnMarker(n))).length;
  const feed = useMemo(() => mergeFeed(notifs, data?.recent_activity || [], 12), [notifs, data]);
  const feedStats = useMemo(() => feedCounts(notifs, data?.recent_activity || []), [notifs, data]);
  const visiblePendingActions = useMemo(
    () => (data?.pending_actions || []).filter((p) => !isDismissed(`action:${p.id}`, actionMarker(p))),
    [data, dismissals], // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (loading) {
    return (
      <div
        className="rounded-lg bg-slate-900/40 border border-slate-700/60 p-12 text-center"
        data-testid="notification-dashboard-loading"
      >
        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400" />
        <p className="text-slate-400 text-sm mt-3">Loading notifications…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="rounded-lg bg-red-950/40 border border-red-900/60 p-6 text-red-200 flex items-center justify-between"
        data-testid="notification-dashboard-error"
      >
        <div>
          <div className="font-semibold mb-1">Could not load dashboard notifications</div>
          <div className="text-xs text-red-300/80">{error || 'No data returned'}</div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={fetchData}
          className="bg-red-500/20 border-red-500/40 text-red-100 hover:bg-red-500/30"
          data-testid="notification-dashboard-retry"
        >
          Retry
        </Button>
      </div>
    );
  }

  const ts = data.top_stats || {};
  const upload = data.upload_reminder || {};
  const showUploadBanner = upload.needs_upload && !isDismissed('banner:upload');

  // Bulletin visibility after per-line dismissals (Josh 08/10: clear all / one by one)
  const visAcctIssues = (data.bulletin?.account_issues || []).filter(
    (d) => !isDismissed(`bacct:${d.deal_uuid}:${d.code}`));
  const visNotPaying = (data.bulletin?.not_paying || []).filter(
    (d) => !isDismissed(`bnp:${d.deal_uuid}`, npMarker(d)));
  const visPaidOff = (data.bulletin?.paid_off_today || []).filter(
    (d) => !isDismissed(`bpo:${d.deal_uuid}`));
  const showBounceSummary = (data.bulletin?.bounced_today || []).length > 0 && !isDismissed('bbounces');
  const showPaymentsSummary = (ts.today_payments_count ?? 0) > 0 && !isDismissed('bpayments');
  const bulletinPairs = [
    ...visAcctIssues.map((d) => [`bacct:${d.deal_uuid}:${d.code}`, todayISO()]),
    ...visNotPaying.map((d) => [`bnp:${d.deal_uuid}`, npMarker(d)]),
    ...visPaidOff.map((d) => [`bpo:${d.deal_uuid}`, todayISO()]),
    ...(showBounceSummary ? [['bbounces', todayISO()]] : []),
    ...(showPaymentsSummary ? [['bpayments', todayISO()]] : []),
  ];
  const bulletinHasContent = bulletinPairs.length > 0;

  return (
    <div className="space-y-4 mb-6" data-testid="notification-dashboard">
      {/* Manual refresh (Josh 08/06 — replaces background polling) */}
      <div className="flex items-center justify-end gap-2 -mb-2">
        {data.generated_at && (
          <span className="text-[11px] text-slate-400" data-testid="dashboard-updated-at">
            Updated {new Date(data.generated_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </span>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2"
          onClick={silentRefresh}
          disabled={refreshing}
          data-testid="dashboard-refresh-btn"
          title="Refresh dashboard"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* TOP STATS */}
      <div
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
        data-testid="dashboard-top-stats"
      >
        <StatTile
          testid="stat-active-deals"
          icon={Briefcase}
          label="Active Deals"
          value={ts.active_deals?.toLocaleString() ?? '—'}
          accent="blue"
          onClick={() => navigate('/deals')}
        />
        <StatTile
          testid="stat-today-net"
          icon={DollarSign}
          label="Collected Today"
          value={formatCurrency(ts.today_net ?? 0)}
          sub={
            ts.today_bounces > 0
              ? `Gross ${formatCurrency(ts.today_gross)} − ${formatCurrency(ts.today_bounces)} bounced`
              : `Gross ${formatCurrency(ts.today_gross ?? 0)}`
          }
          onClick={() => jumpToSection('recent-activity-section')}
          accent="green"
        />
        <StatTile
          testid="stat-mtd-net"
          icon={Activity}
          label="Collected MTD"
          value={formatCurrency(ts.mtd_net ?? 0)}
          sub={
            ts.mtd_bounces > 0
              ? `Gross ${formatCurrency(ts.mtd_gross)} − ${formatCurrency(ts.mtd_bounces)}`
              : `Gross ${formatCurrency(ts.mtd_gross ?? 0)}`
          }
          accent="green"
          onClick={() => jumpToSection('recent-activity-section')}
        />
        <StatTile
          testid="stat-today-bounces"
          icon={XCircle}
          label="Bounces dated today"
          value={ts.today_bounces_count ?? 0}
          sub={ts.today_bounces > 0 ? `−${formatCurrency(ts.today_bounces)}` : 'No bounces'}
          accent="red"
          onClick={() => jumpToSection('todays-bulletin')}
        />
        <StatTile
          testid="stat-matured-overdue"
          icon={Clock}
          label="Matured / Overdue"
          value={ts.matured_overdue ?? 0}
          sub="Past expected end, balance > 0"
          accent="amber"
          onClick={() => jumpToSection('needs-attention-section', 'attn')}
        />
        <StatTile
          testid="stat-bounce-streaks"
          icon={AlertTriangle}
          label="Bounce Streaks"
          value={data.bounce_alerts?.length ?? 0}
          sub={`${data.config?.bounce_threshold ?? 2}+ consecutive bounces`}
          accent="red"
          onClick={() => jumpToSection('bounce-alerts-section', 'bounce')}
        />
      </div>

      {/* TODAY'S BULLETIN — day summary: review bounces / review payments /
          deals paid off. Per-deal bounce lines live in Bounce Alerts. */}
      {bulletinHasContent && (
        <Card className="border-emerald-300 bg-emerald-50" data-testid="todays-bulletin">
          <CardContent className="py-3">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span className="text-sm font-bold text-emerald-800 uppercase tracking-wide">
                  Today's Bulletin
                </span>
              </div>
              <button
                onClick={() => dismissMany(bulletinPairs)}
                className="text-xs text-emerald-700 hover:text-emerald-900 underline"
                data-testid="bulletin-clear-all"
              >
                Clear all
              </button>
            </div>
            <div className="space-y-1">
              {/* URGENT: bank-detail return codes — fix the account NOW */}
              {visAcctIssues.map((d, i) => (
                <div key={`ai-${d.deal_uuid}-${d.code}-${i}`} className="flex items-start justify-between gap-2 text-sm font-semibold text-red-900 bg-red-100 border border-red-300 rounded px-2 py-1">
                  <div>
                    🚨 <span>{d.merchant_name || 'Deal'}</span>
                    {d.deal_shortcode ? (
                      <span className="font-mono text-xs bg-red-200 border border-red-300 rounded px-1 ml-1">{d.deal_shortcode}</span>
                    ) : null}{' '}
                    — {d.code} {d.label} (−{formatCurrency(d.amount)}) — fix the bank account immediately —{' '}
                    <Link to={`/deals/${d.deal_uuid}`} className="underline hover:text-red-700">
                      #{d.deal_id_number}
                    </Link>
                  </div>
                  <button onClick={() => dismiss(`bacct:${d.deal_uuid}:${d.code}`)}
                    className="text-red-400 hover:text-red-700 flex-shrink-0" aria-label="Clear"
                    data-testid={`bulletin-clear-acct-${d.deal_id_number}`}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {/* Payments possibly shut off: non-active status, still owing, gone quiet */}
              {visNotPaying.map((d) => (
                <div key={`np-${d.deal_uuid}`} className="flex items-start justify-between gap-2 text-sm text-amber-900">
                  <div>
                    {d.kind === 'settlement' ? '💼' : '⏸️'}{' '}
                    <span className="font-medium">{d.merchant_name || 'Deal'}</span>
                    {d.deal_shortcode ? (
                      <span className="font-mono text-xs bg-amber-100 border border-amber-200 rounded px-1 ml-1">{d.deal_shortcode}</span>
                    ) : null}{' '}
                    {d.kind === 'settlement' ? (
                      <>
                        is <span className="font-medium">{d.status}</span> but {formatCurrency(d.balance)} is still on the
                        books — was this settled short? Record the settlement discount (or reopen the deal) —{' '}
                      </>
                    ) : (
                      <>
                        is <span className="font-medium">{d.status}</span> with {formatCurrency(d.balance)} still owed and no
                        payment in {d.days_no_payment}d — payments shut off and forgotten? —{' '}
                      </>
                    )}
                    <Link to={`/deals/${d.deal_uuid}`} className="underline font-semibold text-amber-900 hover:text-amber-700">
                      #{d.deal_id_number}
                    </Link>
                  </div>
                  <button onClick={() => dismiss(`bnp:${d.deal_uuid}`, npMarker(d))}
                    className="text-amber-400 hover:text-amber-700 flex-shrink-0" aria-label="Clear"
                    data-testid={`bulletin-clear-np-${d.deal_id_number}`}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {visPaidOff.map((d) => (
                <div key={d.deal_uuid} className="flex items-start justify-between gap-2 text-sm text-emerald-800">
                  <div>
                    🎉 <span className="font-medium">{d.deal_name}</span> has been paid off —{' '}
                    <Link
                      to={`/deals/${d.deal_uuid}`}
                      className="underline font-semibold text-emerald-900 hover:text-emerald-700"
                    >
                      #{d.deal_id_number}
                    </Link>
                  </div>
                  <button onClick={() => dismiss(`bpo:${d.deal_uuid}`)}
                    className="text-emerald-400 hover:text-emerald-700 flex-shrink-0" aria-label="Clear"
                    data-testid={`bulletin-clear-po-${d.deal_id_number}`}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {showBounceSummary && (
                <div className="flex items-start justify-between gap-2 text-sm text-red-800" data-testid="bulletin-review-bounces">
                  <div>
                    ⚠️{' '}
                    <span className="font-medium">
                      {(data.bulletin.bounced_today || []).reduce((t, d) => t + (d.count || 1), 0)} bounce(s) on{' '}
                      {(data.bulletin.bounced_today || []).length} deal(s) posted today — −
                      {formatCurrency((data.bulletin.bounced_today || []).reduce((t, d) => t + (d.amount || 0), 0))}
                    </span>{' '}
                    <button
                      onClick={() => jumpToSection('bounce-alerts-section', 'bounce')}
                      className="underline font-semibold text-red-900 hover:text-red-700"
                      data-testid="bulletin-review-bounces-btn"
                    >
                      Review bounces →
                    </button>
                  </div>
                  <button onClick={() => dismiss('bbounces')}
                    className="text-red-400 hover:text-red-700 flex-shrink-0" aria-label="Clear"
                    data-testid="bulletin-clear-bounces">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              {showPaymentsSummary && (
                <div className="flex items-start justify-between gap-2 text-sm text-emerald-800" data-testid="bulletin-review-payments">
                  <div>
                    💵{' '}
                    <span className="font-medium">
                      {ts.today_payments_count} payment(s) collected today — {formatCurrency(ts.today_gross || 0)} gross
                    </span>{' '}
                    <Link
                      to="/daily-import-review"
                      className="underline font-semibold text-emerald-900 hover:text-emerald-700"
                      data-testid="bulletin-review-payments-link"
                    >
                      Review payments →
                    </Link>
                  </div>
                  <button onClick={() => dismiss('bpayments')}
                    className="text-emerald-400 hover:text-emerald-700 flex-shrink-0" aria-label="Clear"
                    data-testid="bulletin-clear-payments">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* UPLOAD REMINDER BANNER */}
      {showUploadBanner && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-amber-400 bg-amber-500/95 px-4 py-3 shadow-md"
          data-testid="upload-reminder-banner"
        >
          <div className="flex items-center gap-3">
            <Upload className="h-5 w-5 text-slate-900 flex-shrink-0" />
            <div>
              <div className="text-sm font-bold text-slate-900">
                {upload.last_uploaded_at
                  ? `Daily Payments CSV not uploaded in ${Math.round(upload.hours_since)}h`
                  : 'No Daily Payments CSV uploaded yet'}
              </div>
              <div className="text-xs text-slate-800">
                {upload.last_uploaded_at
                  ? `Last: ${upload.filename} (${upload.imported_count} txns) by ${upload.uploaded_by}`
                  : 'Upload daily transactions to keep balances current.'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/daily-import-review">
              <Button
                size="sm"
                className="bg-slate-900 hover:bg-slate-800 text-amber-300 font-semibold"
                data-testid="upload-reminder-cta"
              >
                Upload Now
              </Button>
            </Link>
            <button
              onClick={() => dismiss('banner:upload')}
              className="p-1 rounded hover:bg-amber-600 text-slate-900"
              data-testid="upload-reminder-dismiss"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* 10/08 (Josh: "switch places > pending actions with needs attention"): row 1 = Needs Attention | Notifications,
          row 2 = Pending Actions | Bounce Alerts. items-start so an empty card does not stretch to its neighbour. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:items-start">
        {/* BOUNCE ALERTS — 09/01 (Josh): "SWITCH THE SPOTS FOR BOUNCE ALERTS
            AND NEEDS ATTENTION" — bounce alerts now on the RIGHT. */}
        <SectionCard
          title={`Bounce Alerts (${visibleBounceAlerts.length})`}
          icon={AlertTriangle}
          className="lg:order-4"
          testid="bounce-alerts-section"
          action={
            dismissedBounceCount > 0 && (
              <button
                onClick={() => setShowDismissed((s) => ({ ...s, bounce: !s.bounce }))}
                className="text-xs text-slate-400 hover:text-slate-200 underline"
                data-testid="bounce-show-dismissed-toggle"
              >
                {showDismissed.bounce ? 'Hide dismissed' : `Show dismissed (${dismissedBounceCount})`}
              </button>
            )
          }
        >
          {visibleBounceAlerts.length === 0 ? (
            <EmptyRow text="No bounce streaks and nothing bounced in the last few days." />
          ) : (
            visibleBounceAlerts.slice(0, showDismissed.bounce ? 999 : 8).map((b) => (
              <div
                key={b.deal_id}
                className="flex items-center justify-between px-4 py-3 hover:bg-slate-800/40 transition"
                data-testid={`bounce-alert-${b.deal_id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {b.consecutive_count > 0 ? (
                    <Badge className="bg-red-500/20 text-red-300 border border-red-500/30 font-mono" title="Consecutive bounces since the last cleared payment">
                      {b.consecutive_count}×
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/30 font-mono" title="Bounces posted in the last few days; a later payment has cleared since">
                      +{b.recent_count || 1}
                    </Badge>
                  )}
                  {b.is_new_today && (
                    <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-bold">
                      NEW
                    </Badge>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white truncate">
                      {b.merchant_name || `Deal #${b.deal_id_number}`}
                      {b.merchant_name && (
                        <span className="text-slate-300 ml-2 text-xs">#{b.deal_id_number}</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-200">
                      Bounce dated {b.last_bounce_date}
                      {b.last_bounce_posted_at && ` · posted ${formatRelative(b.last_bounce_posted_at)}`} · −
                      {formatCurrency(b.last_bounce_amount)} · Balance {formatCurrency(b.balance)}
                      {b.paid_since && <span className="text-emerald-400"> · paid since {b.paid_since}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => openBounceEmail(b)}
                    className="p-1.5 rounded text-slate-400 hover:text-amber-300 hover:bg-slate-700"
                    title="Email broker about this bounce"
                    data-testid={`bounce-alert-email-${b.deal_id}`}
                    aria-label="Email broker"
                  >
                    <Mail className="h-4 w-4" />
                  </button>
                  <Link to={`/deals/${b.deal_id}`}>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-slate-300 hover:text-white hover:bg-slate-700"
                      data-testid={`bounce-alert-view-${b.deal_id}`}
                    >
                      View <ChevronRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </Link>
                  <button
                    onClick={() => dismiss(`bounce:${b.deal_id}`, bounceMarker(b))}
                    className="p-1 rounded text-slate-500 hover:text-slate-200 hover:bg-slate-700"
                    data-testid={`bounce-alert-dismiss-${b.deal_id}`}
                    aria-label="Dismiss"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </SectionCard>

        {/* NEEDS ATTENTION — now on the LEFT (order swap, 09/01) */}
        <SectionCard
          title={`Needs Attention (${visibleNeedsAttention.length})`}
          icon={Bell}
          className="lg:order-1"
          testid="needs-attention-section"
          action={
            dismissedAttnCount > 0 && (
              <button
                onClick={() => setShowDismissed((s) => ({ ...s, attn: !s.attn }))}
                className="text-xs text-slate-400 hover:text-slate-200 underline"
                data-testid="attn-show-dismissed-toggle"
              >
                {showDismissed.attn ? 'Hide dismissed' : `Show dismissed (${dismissedAttnCount})`}
              </button>
            )
          }
        >
          {visibleNeedsAttention.length === 0 ? (
            <EmptyRow text="All deals look healthy." />
          ) : (
            visibleNeedsAttention.slice(0, showDismissed.attn ? 999 : 8).map((n) => (
              <div
                key={n.deal_id}
                className="px-4 py-3 hover:bg-slate-800/40 transition cursor-pointer"
                data-testid={`needs-attention-${n.deal_id}`}
                onClick={() => setExpandedAttn((k) => (k === n.deal_id ? null : n.deal_id))}
                role="button"
              >
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white truncate">
                    {n.merchant_name || `Deal #${n.deal_id_number}`}
                    {n.deal_shortcode && (
                      <span className="font-mono text-[10px] text-slate-200 border border-slate-500 rounded px-1 ml-1.5">
                        {n.deal_shortcode}
                      </span>
                    )}
                    {n.merchant_name && (
                      <span className="text-slate-300 ml-2 text-xs">#{n.deal_id_number}</span>
                    )}
                    {n.suggest_legal && (
                      <Badge
                        className="bg-red-500/20 text-red-300 border border-red-500/40 text-[10px] font-bold ml-2"
                        title={`No payment in ${n.days_no_payment}d — consider legal collections. Ask Mila to review this deal first.`}
                        data-testid={`suggest-legal-${n.deal_id}`}
                      >
                        SUGGEST: LEGAL
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-200">
                    <span className="text-amber-300 font-semibold">Why: </span>
                    <span className="text-slate-100">{(n.reasons || []).join(' · ') || 'Flagged by system'}</span>
                    {' '}· <span className="text-slate-100">Balance {formatCurrency(n.balance)}</span>
                    {n.suggest_legal && (
                      <span className="text-red-300"> · {n.days_no_payment}d dark — review with Mila, then send to legal</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Link to={`/deals/${n.deal_id}`} onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-slate-200 hover:text-white hover:bg-slate-700"
                      data-testid={`needs-attention-view-${n.deal_id}`}
                    >
                      View <ChevronRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </Link>
                  <button
                    onClick={(e) => { e.stopPropagation(); dismiss(`attn:${n.deal_id}`, attnMarker(n)); }}
                    className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-700"
                    data-testid={`needs-attention-dismiss-${n.deal_id}`}
                    aria-label="Dismiss"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              {expandedAttn === n.deal_id && (
                <ActionPanel plan={planForAttention(n)} message={
                  planForAttention(n)?.kind === 'zero_balance'
                    ? 'Balance is zero but the deal is still active — open the deal and mark it Paid in Full.'
                    : null
                } />
              )}
              </div>
            ))
          )}
        </SectionCard>

        {/* PENDING ACTIONS */}
        <SectionCard
          title={`Pending Actions (${visiblePendingActions.length})`}
          icon={CheckCircle2}
          className="lg:order-3"
          testid="pending-actions-section"
        >
          {visiblePendingActions.length === 0 ? (
            <EmptyRow text="Nothing pending. Nice work." />
          ) : (
            visiblePendingActions.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between px-4 py-3 hover:bg-slate-800/40 transition"
                data-testid={`pending-action-${p.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Badge
                    className={
                      p.severity === 'warning'
                        ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                        : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                    }
                  >
                    {p.severity}
                  </Badge>
                  <span className="text-sm text-slate-100 truncate">{p.label}</span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Link to={p.action_url}>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-slate-300 hover:text-white hover:bg-slate-700"
                      data-testid={`pending-action-resolve-${p.id}`}
                    >
                      Resolve <ChevronRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </Link>
                  <button
                    onClick={() => dismiss(`action:${p.id}`, actionMarker(p))}
                    className="p-1 rounded text-slate-500 hover:text-slate-200 hover:bg-slate-700"
                    data-testid={`pending-action-dismiss-${p.id}`}
                    aria-label="Dismiss"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </SectionCard>

        {/* NOTIFICATIONS — 10/07 (Josh): Recent Activity + the notifications box in ONE card.
            Reminders, job alerts and other notifications (bell source) interleave with upload
            activity (Daily Import batches + legacy sessions), newest first. */}
        <SectionCard
          title={`Notifications${feedStats.unread ? ` (${feedStats.unread} unread)` : ''}`}
          icon={Bell}
          className="lg:order-2"
          testid="notifications-feed-section"
          action={
            <div className="flex items-center gap-1">
              {feedStats.unread > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-slate-400 hover:text-slate-200 underline mr-2"
                  data-testid="notifications-feed-mark-all"
                >
                  Mark all read
                </button>
              )}
              <Link to="/notifications">
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-slate-300 hover:text-white hover:bg-slate-700 text-xs"
                  data-testid="notifications-feed-view-all"
                >
                  View all
                </Button>
              </Link>
            </div>
          }
        >
          {feed.length === 0 ? (
            <EmptyRow text="No notifications or uploads yet." />
          ) : (
            feed.map((it) => (
              <div
                key={it.key}
                className={`px-4 py-3 hover:bg-slate-800/40 transition cursor-pointer ${
                  it.unread ? 'bg-blue-500/5' : ''
                }`}
                data-testid={`feed-item-${it.key}`}
                onClick={() => openFeedItem(it)}
                role="button"
              >
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  <span className="text-lg leading-none mt-0.5">{it.icon}</span>
                  <div className="min-w-0">
                    <div className={`text-sm truncate ${it.unread ? 'font-semibold text-white' : 'font-medium text-slate-100'}`}>
                      {it.title}
                      {it.unread && <span className="inline-block h-2 w-2 rounded-full bg-blue-400 ml-2 align-middle" />}
                    </div>
                    <div className="text-xs text-slate-300 truncate">
                      {formatRelative(it.at)}
                      {it.detail && ` · ${it.detail}`}
                    </div>
                  </div>
                </div>
                {it.kind === 'upload' ? (
                  <Badge
                    className={
                      it.tone === 'ok'
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                        : it.tone === 'bad'
                          ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                          : it.tone === 'wait'
                            ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                            : 'bg-slate-500/20 text-slate-300 border border-slate-500/30'
                    }
                  >
                    {it.status}
                  </Badge>
                ) : (
                  it.priority !== 'normal' && (
                    <Badge className="bg-orange-500/20 text-orange-300 border border-orange-500/30">{it.priority}</Badge>
                  )
                )}
              </div>
              {expandedFeed === it.key && (
                <ActionPanel plan={planForFeedItem(it)} message={it.kind === 'notification' ? it.detail : null} />
              )}
              </div>
            ))
          )}
        </SectionCard>
      </div>

      {/* BOUNCE NOTICE EMAIL DIALOG */}
      <Dialog open={!!emailDlg} onOpenChange={(open) => !open && setEmailDlg(null)}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-amber-300" />
              {emailDlg?.mode === 'outreach' ? `Email — ${EMAIL_LABEL[emailDlg.kind] || emailDlg.kind} — Deal #${emailDlg?.dealNum ?? ''}` : `Bounce Notice — Deal #${emailDlg?.dealNum}`}
            </DialogTitle>
          </DialogHeader>
          {emailDlg?.loading ? (
            <div className="py-8 text-center text-sm text-slate-400">Building preview…</div>
          ) : emailDlg ? (
            <div className="space-y-3">
              {(emailDlg.warnings || []).map((w, i) => (
                <div
                  key={i}
                  className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1"
                >
                  ⚠ {w}
                </div>
              ))}
              {emailDlg.mode === 'outreach' && (emailDlg.recipients || []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {emailDlg.recipients.map((r) => (
                    <button key={r.role} type="button" onClick={() => openOutreachEmail(emailDlg.dealUuid, emailDlg.kind, r.role)}
                      className={`text-xs rounded border px-2 py-1 ${emailDlg.toRole === r.role ? 'border-amber-400 text-amber-200' : 'border-slate-600 text-slate-300 hover:text-white'}`}>
                      {r.label}{r.email ? ` <${r.email}>` : ' (no email)'}
                    </button>
                  ))}
                </div>
              )}
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wide">{emailDlg.mode === 'outreach' ? 'To' : 'To (broker)'}</label>
                <Input
                  value={emailDlg.to}
                  onChange={(e) => setEmailDlg((d) => ({ ...d, to: e.target.value }))}
                  placeholder="broker@example.com"
                  className="bg-slate-800 border-slate-600 text-slate-100"
                  data-testid="bounce-email-to"
                />
              </div>
              {emailDlg.mode === 'outreach' && (
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wide">Cc (optional, comma-separated)</label>
                  <Input value={emailDlg.cc || ''} onChange={(e) => setEmailDlg((d) => ({ ...d, cc: e.target.value }))}
                    className="bg-slate-800 border-slate-600 text-slate-100" data-testid="outreach-email-cc" />
                </div>
              )}
              <SenderSelect sender={emailDlg.sender} value={emailDlg.senderId} onChange={(v) => setEmailDlg((d) => ({ ...d, senderId: v }))} dark />
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wide">Subject</label>
                <Input
                  value={emailDlg.subject}
                  onChange={(e) => setEmailDlg((d) => ({ ...d, subject: e.target.value }))}
                  className="bg-slate-800 border-slate-600 text-slate-100"
                  data-testid="bounce-email-subject"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wide">Message</label>
                <Textarea
                  value={emailDlg.body}
                  onChange={(e) => setEmailDlg((d) => ({ ...d, body: e.target.value }))}
                  rows={12}
                  className="bg-slate-800 border-slate-600 text-slate-100 font-mono text-sm"
                  data-testid="bounce-email-body"
                />
              </div>
              <div className="flex items-center justify-between pt-1">
                <span className="text-xs text-slate-500">
                  Template editable in Settings → Email Templates
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => setEmailDlg(null)}
                    className="text-slate-300 hover:text-white hover:bg-slate-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={sendEmail}
                    disabled={emailDlg.sending || !emailDlg.to}
                    className="bg-amber-600 hover:bg-amber-500 text-white"
                    data-testid="bounce-email-send"
                  >
                    {emailDlg.sending ? 'Sending…' : 'Send Email'}
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationDashboard;
